import { type FunctionComponent, type PropsWithChildren } from 'react';
import { useInView } from 'react-intersection-observer';
import { Carousel } from '../../../../components/carousel/carousel.component';
import { StyledLink } from '../../../../components/common-components/link/styled-link.component';
import { QuickStartImageGrid } from '../../../../components/quickstart-image-grid/quickstart-image-grid.component';
import ArrowNarrowRightIconIcon from '../../../../components/svgs/untitled-ui/arrow-narrow-right.svg';
import { SHOP_BY_LOOK_LINK } from '../../../../constants/links';
import { SectionTitleEnum } from '../../../../constants/project/shop-by-look';
import { type QuickStartImageGridLook } from '../../../../types/project.types';
import { favoritesImageContainerWidth, trendsImageContainerWidth } from './shop-by-look-section-wrapper.css';

export type ShopByLookSectionProps = {
	looks: QuickStartImageGridLook[];
	fetchMoreLooks?: () => void;
};

export const TrendsAndInfluences: FunctionComponent<ShopByLookSectionProps> = ({ looks }) => (
	<Carousel>
		<QuickStartImageGrid
			looks={looks}
			containerClassName="flex flex-row pv4"
			className={`pa1 ${trendsImageContainerWidth}`}
			isShopByLookPage={true}
			isCarousel={true}
			keyPrefix="trends"
			cloudinaryOptions={{ height: 202 }}
		/>
	</Carousel>
);

export const MyFavorites: FunctionComponent<ShopByLookSectionProps> = ({ looks }) => (
	<Carousel>
		<QuickStartImageGrid
			looks={looks}
			containerClassName="flex flex-nowrap flex-row flex ga2 ga0-l ma2 pv2"
			className={`pa1 ${favoritesImageContainerWidth}`}
			isShopByLookPage={true}
			isCarousel={true}
			keyPrefix="favorites"
		/>
	</Carousel>
);

export const FeaturedLooks: FunctionComponent<ShopByLookSectionProps> = ({ looks, fetchMoreLooks }) => {
	const { ref: inViewRef } = useInView({
		onChange: (inView) => {
			if (inView && fetchMoreLooks) {
				fetchMoreLooks();
			}
		},
		// for a smoother scrolling experience - will call query when ref is 80px below viewport
		rootMargin: '0px 0px 80px 0px'
	});

	return (
		<>
			<QuickStartImageGrid
				looks={looks}
				containerClassName="flex flex-wrap flex-row ga2 ga0-l ma2"
				className="w-third-l pa2-l"
				showTitle={false}
				isShopByLookPage={true}
				isCarousel={false}
				keyPrefix="featured"
			/>
			{/* dummy div for observer */}
			<div style={{ height: '0px' }} ref={inViewRef}></div>
		</>
	);
};

export type ShopByLookSectionWrapperProps = {
	sectionTitle: SectionTitleEnum;
	fetchMoreLooks?: () => void;
	numLooks?: number;
};

export const ShopByLookSectionWrapper: FunctionComponent<PropsWithChildren<ShopByLookSectionWrapperProps>> = ({
	sectionTitle,
	children,
	numLooks
}) => {
	const className =
		sectionTitle === SectionTitleEnum.FEATURED_LOOKS
			? ''
			: 'mb4-ns bg-theme-grey-lighter-ns ba-ns b--theme-grey-light bg-theme-grey-lighter pa3 mt3';

	return (
		<div className="relative" data-testid="sbl-section-wrapper">
			<h2 className="flex theme-grey-darker font-light f2-ns f3 lh-title-ns lh-solid w-100 justify-center mb3">{sectionTitle}</h2>
			{sectionTitle === SectionTitleEnum.MY_FAVORITES && numLooks && numLooks > 3 && (
				<StyledLink url={`${SHOP_BY_LOOK_LINK}?favorites=true`} className="absolute top-0 right-0 mt1 mt3-ns">
					<div className="flex items-center" data-testid="view-all-button">
						<span className="mr1">View All</span>
						<ArrowNarrowRightIconIcon />
					</div>
				</StyledLink>
			)}
			<div className={className}>{children}</div>
		</div>
	);
};
